import React from "react";
import OrderedModuleEditor from "@/components/common/OrderedModuleEditor";
import MediaImageField from "@/components/common/MediaImageField";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <Label className="text-xs text-white/50">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
  </div>
);

const tagsToString = (tags) => (Array.isArray(tags) ? tags.join(", ") : (tags || ""));
const stringToTags = (value) =>
  String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export default function Portfolio() {
  return (
    <OrderedModuleEditor
      resource="portfolio"
      singular="project"
      title="Portfolio"
      subtitle="Drag to reorder case studies. Upload covers and edit details in the inspector."
      titleKey="title"
      defaultValues={{
        order: 1,
        title: "",
        slug: "",
        type: "",
        industry: "",
        desc: "",
        cover_image: "",
        services: [],
        status: "completed",
      }}
      getItemLabel={(it, idx) => it.title || `Project ${idx + 1}`}
      renderPreviewItem={(it) => {
        const cover = resolveMediaUrl(it.cover_image);
        return (
          <div className="flex gap-3 items-start">
            {cover ? (
              <img src={cover} alt="" className="w-20 h-14 rounded-lg object-cover shrink-0 bg-white/5" />
            ) : (
              <span className="w-20 h-14 rounded-lg glass flex items-center justify-center text-[10px] text-white/40 shrink-0">
                No cover
              </span>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/40">{it.type || "Untitled type"}</div>
              <div className="text-sm font-medium text-white mt-0.5">{it.title || "Untitled"}</div>
              <p className="text-xs text-white/50 mt-1 line-clamp-2">{it.desc || ""}</p>
              {it.industry ? <div className="text-[11px] text-white/40 mt-1">{it.industry}</div> : null}
            </div>
          </div>
        );
      }}
      renderInspector={(item, patch) => (
        <>
          <Field label="Title">
            <Input
              value={item.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Slug" hint="URL-friendly id (optional)">
            <Input
              value={item.slug || ""}
              onChange={(e) => patch({ slug: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Type / Category" hint="e.g. Website Development, Branding & Advertising">
            <Input
              value={item.type || ""}
              onChange={(e) => patch({ type: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Industry" hint="e.g. Education / Local Business">
            <Input
              value={item.industry || ""}
              onChange={(e) => patch({ industry: e.target.value })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Description">
            <Textarea
              value={item.desc || item.description || ""}
              onChange={(e) => patch({ desc: e.target.value })}
              rows={4}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <MediaImageField
            label="Cover Image"
            value={item.cover_image || ""}
            onChange={(url) => patch({ cover_image: url })}
            folder="portfolio"
            aspectClass="h-28"
            hint="Shown on portfolio cards. Prefer uploading a durable image."
          />
          <Field label="Services Used" hint="Comma-separated, e.g. Logo direction, Campaign creative">
            <Input
              value={tagsToString(item.services)}
              onChange={(e) => patch({ services: stringToTags(e.target.value) })}
              className="bg-black/20 border-white/10 text-white"
            />
          </Field>
          <Field label="Status">
            <select
              value={item.status || "completed"}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full bg-black/20 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </Field>
        </>
      )}
    />
  );
}
